// AdminAppSettings — admin-only panel that toggles which social-proof
// surfaces show up for school users.
//
// Currently controls:
//   - Top sticky Live Activity ticker (the 3s show / 12s hide rotation).
//   - Bottom-right Live Activity widget on the school dashboard
//     ("🔥 Live Activity · Updated every 30s").
//
// Backed by /api/admin/app/settings (admin POST) and /api/app/settings
// (logged-in GET). The mutateAppSettings() helper pushes the new value into
// the global store the moment the POST succeeds so the change is visible
// across the app instantly without waiting for the 60s poll.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Radio, Eye, EyeOff, Loader2, Bell, IndianRupee, Save, MessageSquareText, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAppSettings, mutateAppSettings } from "@/lib/appSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAGS = [
  {
    key: "liveActivityTickerEnabled",
    icon: <Radio className="h-4 w-4" />,
    title: "Top Live Activity Ticker",
    desc: "The thin rotating bar above every page (3s show / 12s hide rhythm).",
    surface: "Every logged-in page",
  },
  {
    key: "liveActivityWidgetEnabled",
    icon: <Activity className="h-4 w-4" />,
    title: "Live Activity Widget on Dashboard",
    desc: "The “🔥 Live Activity · Updated every 30s” card on the school dashboard sidebar.",
    surface: "School dashboard only",
  },
  {
    key: "liveActivityToastEnabled",
    icon: <Bell className="h-4 w-4" />,
    title: "Bottom-Right Live Activity Toast",
    desc: "The popup card that slides in from the bottom-right showing other schools’ activity (e.g. \"a partner school just generated a Quarterly UC\").",
    surface: "Every logged-in page",
  },
  {
    key: "teachersMessageEnabled",
    icon: <MessageSquareText className="h-4 w-4" />,
    title: "“For our teachers” Odia Message",
    desc: "The Odia welcome card + one-time popup that invites teachers to send WhatsApp data for bill/UC preparation. Edit the text below.",
    surface: "School dashboard (card + first-visit popup)",
  },
];

export default function AdminAppSettings() {
  const settings = useAppSettings();
  const [busyKey, setBusyKey] = useState("");

  const toggle = async (key) => {
    const next = !settings[key];
    setBusyKey(key);
    try {
      const r = await axios.post(`${API}/admin/app/settings`, { [key]: next });
      // Server returns the full settings doc — sync everything.
      mutateAppSettings(r.data || { [key]: next });
      toast.success(`${next ? "Showing" : "Hiding"} for all users`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update setting");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <section
      data-testid="admin-app-settings"
      className="rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6"
    >
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-clay/10 text-clay">
          <Eye className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold tracking-tight text-ink-900">
            Display Settings
          </h3>
          <p className="text-xs text-ink-700">
            Show or hide the live social-proof surfaces for every school user. Changes apply within ~60 seconds.
          </p>
        </div>
      </header>

      <ul className="divide-y divide-border">
        {FLAGS.map((f) => {
          const on = !!settings[f.key];
          const busy = busyKey === f.key;
          return (
            <li
              key={f.key}
              data-testid={`admin-setting-${f.key}`}
              className="flex flex-wrap items-start gap-3 py-3 first:pt-0 last:pb-0"
            >
              <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${on ? "bg-moss/15 text-moss-dark" : "bg-sand-100 text-ink-700"}`}>
                {on ? f.icon : <EyeOff className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink-900">{f.title}</p>
                <p className="mt-0.5 text-xs text-ink-700">{f.desc}</p>
                <p className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-ink-700/80">
                  Surface: {f.surface}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[0.65rem] font-semibold uppercase tracking-[0.16em] ${on ? "text-moss-dark" : "text-ink-700"}`}>
                  {on ? "ON" : "OFF"}
                </span>
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin text-clay" />
                ) : (
                  <Switch
                    checked={on}
                    onCheckedChange={() => toggle(f.key)}
                    aria-label={`Toggle ${f.title}`}
                    data-testid={`admin-setting-toggle-${f.key}`}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <RazorpayLinksEditor settings={settings} />

      <TeachersMessageEditor settings={settings} />
    </section>
  );
}

// iter108 — all payment flows removed. RazorpayLinksEditor is now a noop so
// the AdminAppSettings UI doesn't crash on stale settings documents that
// still carry the razorpayLinks field.
function RazorpayLinksEditor() { return null; }

// iter139 — edit the Odia "For our teachers" message text in-line. Lives
// inside Settings tab so a single page controls visibility (above toggle)
// AND content. Saving sends a flat `teachersMessageContent` object.
const TM_FIELDS = [
  { key: "title", label: "Title (bold, top of card)", rows: 2 },
  { key: "greeting", label: "Greeting", rows: 1 },
  { key: "intro", label: "Intro paragraph", rows: 4 },
  { key: "offerHead", label: "Offer headline", rows: 2 },
  { key: "offerBody", label: "Offer body", rows: 4 },
  { key: "whatHead", label: "“What we do” headline", rows: 1 },
  { key: "benefits", label: "Benefits (one per line)", rows: 4, isList: true },
  { key: "negHead", label: "Highlights / “Negatives” headline", rows: 1 },
  { key: "negs", label: "Highlights (one per line)", rows: 4, isList: true },
  { key: "cta", label: "Call-to-action line", rows: 3 },
  { key: "waNumber", label: "WhatsApp number (with country code, digits only)", rows: 1 },
];

function tmToForm(c) {
  if (!c) return {};
  const out = {};
  for (const f of TM_FIELDS) {
    const v = c[f.key];
    if (f.isList) out[f.key] = Array.isArray(v) ? v.join("\n") : (v || "");
    else out[f.key] = v || "";
  }
  return out;
}

function formToPatch(form) {
  const out = {};
  for (const f of TM_FIELDS) {
    const v = form[f.key];
    if (f.isList) {
      out[f.key] = (v || "").split("\n").map((x) => x.trim()).filter(Boolean);
    } else {
      out[f.key] = (v || "").trim();
    }
  }
  return out;
}

function TeachersMessageEditor({ settings }) {
  const [form, setForm] = useState(tmToForm(settings.teachersMessageContent));
  const [saving, setSaving] = useState(false);

  // Re-sync when settings poll lands AFTER first paint.
  useEffect(() => {
    setForm(tmToForm(settings.teachersMessageContent));
  }, [settings.teachersMessageContent]);

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/app/settings`, {
        teachersMessageContent: formToPatch(form),
      });
      mutateAppSettings(r.data || {});
      toast.success("Teachers message updated — visible to schools within ~60s");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save message");
    } finally {
      setSaving(false);
    }
  };

  const onResetDefaults = async () => {
    if (!confirm("Reset the teachers message to the default Odia text?")) return;
    setSaving(true);
    try {
      // Sending empty strings/lists falls back to backend defaults.
      const empty = {};
      for (const f of TM_FIELDS) empty[f.key] = f.isList ? [] : "";
      const r = await axios.post(`${API}/admin/app/settings`, { teachersMessageContent: empty });
      mutateAppSettings(r.data || {});
      setForm(tmToForm((r.data || {}).teachersMessageContent));
      toast.success("Reset to default text");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset");
    } finally {
      setSaving(false);
    }
  };

  const enabled = settings.teachersMessageEnabled !== false;

  return (
    <section
      data-testid="teachers-message-editor"
      className="mt-6 rounded-2xl border border-border bg-sand-50/50 p-4 sm:p-5"
    >
      <header className="mb-3 flex flex-wrap items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-clay/15 text-clay">
          <MessageSquareText className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold tracking-tight text-ink-900">
            “For our teachers” message — content editor
          </h3>
          <p className="text-xs text-ink-700">
            Edit any field then click Save. Use the toggle above to show or hide the entire card + popup on the school dashboard.
            {!enabled && <span className="ml-1 font-semibold text-clay-dark">(Currently hidden from users.)</span>}
          </p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        {TM_FIELDS.map((f) => (
          <label key={f.key} className={f.rows > 2 || f.isList ? "sm:col-span-2 block" : "block"}>
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-700">{f.label}</span>
            {f.rows <= 1 ? (
              <Input
                value={form[f.key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                data-testid={`teachers-msg-field-${f.key}`}
                className="mt-1"
              />
            ) : (
              <Textarea
                value={form[f.key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                rows={f.rows}
                data-testid={`teachers-msg-field-${f.key}`}
                className="mt-1"
              />
            )}
          </label>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="ghost"
          onClick={onResetDefaults}
          disabled={saving}
          data-testid="teachers-msg-reset-btn"
          className="text-ink-700"
        >
          <RotateCcw className="mr-1 h-4 w-4" /> Reset to defaults
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-clay text-white hover:bg-clay-dark"
          data-testid="teachers-msg-save-btn"
        >
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
          Save message
        </Button>
      </div>
    </section>
  );
}

